import { EMPTY_FIELD } from 'constants/emptyField';

import { useEffect, useState } from 'react';

import CopyAllIcon from '@mui/icons-material/CopyAll';
import { SxProps } from '@mui/material';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import get from 'lodash/get';
import { useRecordContext } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { IconButton } from 'shared/mui/IconButtons';
import { Tooltip } from 'shared/mui/Tooltip';
import { FunctionField, FunctionFieldProps } from 'shared/react-admin/Fields';

import { TypographyStyled } from './CopyToClipboardField.styled';

export type CopyToClipboardFieldProps = {
  source?: string;
  value?: string;
  isTrimValue?: boolean;
  underline?: boolean;
  getValue?: (value: string) => string;
  sx?: SxProps;
} & Omit<FunctionFieldProps, 'render' | 'sx'>;

export const CopyToClipboardField: React.FC<CopyToClipboardFieldProps> = ({
  onClick,
  source,
  sx,
  value,
  isTrimValue,
  underline = false,
  getValue,
  ...rest
}) => {
  const record = useRecordContext();
  const recordValue = value || (source && get(record, source));
  const [currentValue, setCurrentValue] = useState(recordValue);

  const { tooltipText, handleCopy } = useCopyToClipboard(recordValue);

  useEffect(() => {
    if (isTrimValue && source && record?.[source as string]?.length > 12) {
      setCurrentValue(`${currentValue.slice(0, 6)}...${currentValue.slice(-4)}`);
    }
  }, []);

  if (!currentValue) {
    return <span>{EMPTY_FIELD}</span>;
  }

  const renderTitle = () => (
    <TypographyStyled sx={sx} underline={underline} variant="body2">
      {getValue ? getValue(currentValue) : currentValue}
    </TypographyStyled>
  );

  return (
    <FunctionField
      render={() => (
        <Box
          onClick={onClick}
          sx={{ position: 'relative', gap: '4px', display: 'flex', alignItems: 'center' }}
        >
          {recordValue === currentValue ? (
            renderTitle()
          ) : (
            <Tooltip arrow title={recordValue}>
              {renderTitle()}
            </Tooltip>
          )}
          <Tooltip TransitionProps={{ timeout: 0 }} open={!!tooltipText} title={tooltipText}>
            <IconButton onClick={handleCopy} size="small">
              <CopyAllIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {...rest}
    />
  );
};
